import React, { useState } from 'react';
import { documentDownloadService } from '../../../services/documentDownloadService';
import {
  formAnswerCanPreview,
  parseSupportMessageForm,
  SupportMessageFormAnswer,
} from '../../../utils/supportMessageForm';

interface WorkspaceFormCardProps {
  content?: string | null;
  contentAttributes?: string | null;
}

function waitingCopy(content: string | null | undefined, title: string): string
{
  const lines = (content || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const rest = lines[0] === title ? lines.slice(1) : lines;
  if (!rest.length)
  {
    return 'Aguardando o visitante preencher.';
  }

  return rest.join('\n');
}

function saveBlob(blob: Blob, fileName: string)
{
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function viewBlob(blob: Blob)
{
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export const WorkspaceFormCard: React.FC<WorkspaceFormCardProps> = ({
  content,
  contentAttributes,
}) => {
  const form = parseSupportMessageForm(contentAttributes);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  if (!form.isForm)
  {
    return null;
  }

  const title = form.formTitle || 'Formulário';
  const badge = form.submitted
    ? {
        label: 'Respondido',
        className: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      }
    : {
        label: 'Aguardando',
        className: 'bg-amber-100 text-amber-800 border-amber-200',
      };

  async function openFile(answer: SupportMessageFormAnswer, mode: 'view' | 'download')
  {
    if (!answer.documentId)
    {
      return;
    }

    const key = `${mode}:${answer.documentId}`;
    setPendingKey(key);
    setFileError(null);
    try
    {
      const blob = await documentDownloadService.downloadDocument(answer.documentId);
      const fileName = answer.fileName || answer.value || 'arquivo';
      if (mode === 'view')
      {
        viewBlob(blob);
      }
      else
      {
        saveBlob(blob, fileName);
      }
    }
    catch
    {
      setFileError('Não foi possível abrir o arquivo. Tente novamente.');
    }
    finally
    {
      setPendingKey(null);
    }
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-gray-50 px-4 py-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-gray-900">{title}</p>
          <p className="text-xs text-gray-500">Formulário no chat</p>
        </div>
        <span
          className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-1 text-xs font-medium ${badge.className}`}
        >
          {badge.label}
        </span>
      </div>
      <div className="px-4 py-3">
        {form.submitted && form.answers.length > 0 ? (
          <dl className="divide-y divide-gray-100">
            {form.answers.map((answer) => {
              const isFile = Boolean(answer.fileName || answer.contentType);
              const fileName = answer.fileName || answer.value;
              return (
                <div key={`${answer.label}-${fileName}`} className="py-3 first:pt-0 last:pb-0">
                  <dt className="text-xs font-medium text-gray-500">{answer.label}</dt>
                  <dd className="mt-1">
                    {isFile ? (
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <p className="min-w-0 break-all text-sm font-medium text-gray-900">{fileName}</p>
                        {answer.documentId ? (
                          <div className="flex shrink-0 gap-2">
                            {formAnswerCanPreview(answer) ? (
                              <button
                                type="button"
                                className="inline-flex min-h-11 items-center rounded-lg border border-gray-200 bg-white px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-wait disabled:opacity-60"
                                disabled={pendingKey !== null}
                                aria-label={`Ver ${fileName}`}
                                onClick={() => openFile(answer, 'view')}
                              >
                                {pendingKey === `view:${answer.documentId}` ? 'Abrindo...' : 'Ver'}
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="inline-flex min-h-11 items-center rounded-lg bg-blue-600 px-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-wait disabled:opacity-60"
                              disabled={pendingKey !== null}
                              aria-label={`Baixar ${fileName}`}
                              onClick={() => openFile(answer, 'download')}
                            >
                              {pendingKey === `download:${answer.documentId}` ? 'Baixando...' : 'Baixar'}
                            </button>
                          </div>
                        ) : (
                          <p className="text-xs text-amber-700">Arquivo não disponível para download.</p>
                        )}
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap break-words text-sm text-gray-900">{answer.value}</p>
                    )}
                  </dd>
                </div>
              );
            })}
          </dl>
        ) : (
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
            {form.submitted
              ? (content?.trim() || 'Resposta recebida.')
              : waitingCopy(content, title)}
          </p>
        )}
        {fileError ? (
          <p className="mt-3 text-sm text-red-600" role="alert">{fileError}</p>
        ) : null}
      </div>
    </div>
  );
};
